// Eén vorm voor een taak, wat het scherm ook aanlevert.
//
// Vier schermen gaven elk hun eigen taakvorm door aan hun eigen detailvenster:
//   - ruwe Airtable-vorm `{ id, fields: { NaamTaak, PrioNr, … } }` (projectdetail, Mijn taken,
//     de spiegel /api/v1/mirror/tasks);
//   - omgezette vorm `{ id, name, prio, startDate, … }` (Dagstart, Rooster);
//   - schermvorm met `_raw` erbij (Vandaag, Weekplan: `{ title, priority, _raw: { id, fields } }`).
// `naarTaak` maakt daar één taak van, zodat het TaakPaneel maar één vorm kent.
//
// Belangrijk verschil tussen de bronnen: in de Airtable-vorm betekent een ontbrekend veld
// "leeg" (de spiegel laat lege velden weg); in de omgezette vorm betekent een ontbrekende
// sleutel "weet ik niet" (het Rooster levert bijvoorbeeld geen Opmerking mee). Daarom geeft
// de omgezette vorm `undefined` voor wat hij niet kent, zodat `vulAan` het uit een vers
// opgehaald record kan aanvullen — anders zou een lege Opmerking in beeld staan en zou
// bewerken een bestaande opmerking overschrijven.

import { isDagstaatTaak } from '../../utils/taakStatus';

/** PrioNr is prioriteit én status tegelijk. Exact de keuzes van het singleSelect-veld. */
export const STATUS_OPTIES = [
  'Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Geen prio', 'Deadline',
  'Wacht op feedback', 'Nog niet van toepassing', 'Vanallesuurtje', 'Afspraak',
  'Draaidag', 'Vrij/intern', 'Intern', 'Afgerond', 'Opgestuurd', 'Gearchiveerd',
];

/** Keuzes van het singleSelect-veld `Type` ('1.0' en 'Isa' zijn importrestanten). */
export const TYPE_OPTIES = [
  'Preproductie', 'Draaidag', 'Montage', 'Animatie', 'Deadline', 'Vrije dag',
  'Intern', 'Illustratie', 'Projectafronding', 'Archief', 'IT', 'Compliance',
  'Reparatie', 'Marketing', 'Social Strategie', 'Afspraak', 'Regie',
];

/** Keuzes van `TypeDeadline`; Hard en Klant tellen als harde belofte. */
export const DEADLINE_SOORTEN = ['Hard', 'Klant', 'Zacht', 'Intern'];

const AFGEROND_STATUSSEN = new Set(['Afgerond', 'Opgestuurd', 'Gearchiveerd']);

const lijst = (v) => {
  if (v === undefined || v === null || v === '') return [];
  return Array.isArray(v) ? v.filter((x) => x !== null && x !== undefined && x !== '') : [v];
};
const eerste = (v) => lijst(v)[0] ?? null;
/** "2028-04-03T00:00:00" en "2028-04-03" → "2028-04-03"; leeg → null. */
export const dag = (v) => {
  const s = String(eerste(v) ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};
const getal = (v) => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};
const tekst = (v) => {
  const w = eerste(v);
  return w === null || w === undefined ? '' : String(w);
};
/** Een sleutel die de omgezette vorm niet heeft, blijft `undefined` ("onbekend"). */
const kent = (o, ...sleutels) => sleutels.some((k) => Object.prototype.hasOwnProperty.call(o, k));
const pak = (o, sleutels, maak) => {
  if (!kent(o, ...sleutels)) return undefined;
  const k = sleutels.find((s) => o[s] !== undefined && o[s] !== null && o[s] !== '') ?? sleutels[0];
  return maak(o[k]);
};

/** Ruwe Airtable-/spiegelvorm → taak. Ontbrekend = leeg. */
function uitVelden(id, f) {
  const status = tekst(f.PrioNr);
  const assigneeIds = lijst(f.Assignee).map(String);
  return {
    id,
    naam: tekst(f.NaamTaak),
    status,
    afgerond: f.Afgerond === true || AFGEROND_STATUSSEN.has(status),
    type: tekst(f.Type),
    start: dag(f.StartDateUitvoerder),
    eind: dag(f.EndDateUitvoerder),
    deadline: dag(f.Deadline),
    deadlineSoort: tekst(f.TypeDeadline),
    uren: getal(f.UrenWerk),
    opmerking: tekst(f.Opmerking ?? f.Opmerkingen),
    projectId: eerste(f.Project),
    projectNaam: tekst(f['Prnr en Naam']) || tekst(f.ProjectNummer),
    deliverableId: eerste(f.Deliverable),
    deliverableNaam: tekst(f['Naam Deliverable'] ?? f['Naam van Deliverable']),
    fase: tekst(f.Fase),
    assigneeIds,
    assigneeNamen: lijst(f['Voornaam Assignee']).map(String).filter((n) => !n.startsWith('rec')),
    subtaken: lijst(f['Sub tasks']).length,
    openSubtaken: Boolean(f['Open subtaken']),
    aangemaakt: dag(f.Created) || dag(f.airtable_created),
  };
}

/** Omgezette vorm (Dagstart, Rooster, …) → taak. Ontbrekend = onbekend (`undefined`). */
function uitTaakvorm(t) {
  const status = pak(t, ['prio', 'status', 'priority'], tekst);
  const assigneeIds = pak(t, ['assigneeIds'], (v) => lijst(v).map(String));
  let afgerond;
  if (kent(t, 'isComplete', 'afgerond')) afgerond = Boolean(t.isComplete ?? t.afgerond);
  if (status && AFGEROND_STATUSSEN.has(status)) afgerond = true;
  return {
    id: t.id,
    naam: pak(t, ['name', 'naam', 'title'], tekst),
    status,
    afgerond,
    type: pak(t, ['type'], tekst),
    start: pak(t, ['startDate', 'start'], dag),
    eind: pak(t, ['endDate', 'eind'], dag),
    deadline: pak(t, ['deadline'], dag),
    deadlineSoort: pak(t, ['deadlineType', 'deadlineSoort'], tekst),
    uren: pak(t, ['hours', 'uren'], getal),
    opmerking: pak(t, ['opmerking', 'notes'], tekst),
    projectId: pak(t, ['projectId'], eerste),
    projectNaam: pak(t, ['project', 'projectNaam', 'projectName'], tekst),
    deliverableId: pak(t, ['deliverableId'], eerste),
    deliverableNaam: pak(t, ['deliverable', 'deliverableNaam'], tekst),
    fase: pak(t, ['fase'], tekst),
    assigneeIds,
    assigneeNamen: pak(t, ['assignees', 'assignee'], (v) => lijst(v).map(String)),
    subtaken: undefined,
    openSubtaken: undefined,
    aangemaakt: undefined,
  };
}

/**
 * Elke taakvorm → `{ id, naam, status, afgerond, type, start, eind, deadline, deadlineSoort,
 * uren, opmerking, projectId, projectNaam, deliverableId, deliverableNaam, fase,
 * assigneeIds, assigneeNamen, subtaken, openSubtaken, aangemaakt }`.
 * De schermvorm met `_raw` (Vandaag, Weekplan) gaat via het ruwe record, met de
 * optimistische schermwaarden (titel, prio, datum) eroverheen.
 */
export function naarTaak(record) {
  if (!record || typeof record !== 'object') return null;
  if (record.fields && typeof record.fields === 'object') {
    const t = uitVelden(record.id, record.fields);
    // `_onvolledig`: een net aangemaakt record met alleen de meegestuurde velden (POST-antwoord).
    // Wat ontbreekt is dan "onbekend", niet "leeg" — zodat het paneel het vers kan aanvullen.
    if (record._onvolledig) {
      Object.entries(VELD_NAAR_SLEUTEL).forEach(([veld, sleutel]) => {
        if (!(veld in record.fields)) t[sleutel] = undefined;
      });
      if (!('Assignee' in record.fields)) t.assigneeNamen = undefined;
    }
    return t;
  }
  if (record._raw?.fields) {
    const basis = uitVelden(record._raw.id || record._airtableId || record.id, record._raw.fields);
    const scherm = uitTaakvorm(record);
    return {
      ...basis,
      ...Object.fromEntries(Object.entries(scherm).filter(([k, v]) => v !== undefined && k !== 'id')),
      id: basis.id,
    };
  }
  return uitTaakvorm(record);
}

/** Vult wat `taak` niet kent (`undefined`) aan uit `vers` (een vers opgehaald record). */
export function vulAan(taak, vers) {
  if (!taak) return vers || null;
  if (!vers) return taak;
  const uit = { ...taak };
  Object.keys(vers).forEach((k) => {
    if (uit[k] === undefined) uit[k] = vers[k];
  });
  return uit;
}

// Airtable-veld → sleutel in de taak, voor lokaal bijwerken en voor de ongedaan-waarde.
const VELD_NAAR_SLEUTEL = {
  NaamTaak: 'naam', PrioNr: 'status', Afgerond: 'afgerond', Type: 'type',
  StartDateUitvoerder: 'start', EndDateUitvoerder: 'eind', Deadline: 'deadline',
  TypeDeadline: 'deadlineSoort', UrenWerk: 'uren', Opmerking: 'opmerking', Assignee: 'assigneeIds',
};

/** Past een PATCH-body toe op de taak (voor direct beeld na opslaan en na ongedaan maken). */
export function pasToe(taak, velden) {
  if (!taak) return taak;
  const uit = { ...taak };
  Object.entries(velden || {}).forEach(([veld, waarde]) => {
    const k = VELD_NAAR_SLEUTEL[veld];
    if (!k) return;
    if (k === 'uren') uit.uren = getal(waarde);
    else if (k === 'afgerond') uit.afgerond = Boolean(waarde);
    else if (k === 'assigneeIds') uit.assigneeIds = lijst(waarde).map(String);
    else if (['start', 'eind', 'deadline'].includes(k)) uit[k] = dag(waarde);
    else uit[k] = waarde === null || waarde === undefined ? '' : String(waarde);
  });
  if (velden && 'PrioNr' in velden && AFGEROND_STATUSSEN.has(velden.PrioNr)) uit.afgerond = true;
  if (velden && 'Assignee' in velden) uit.assigneeNamen = undefined; // namen zijn een lookup; opnieuw afleiden
  return uit;
}

/**
 * Kent de taak de huidige waarde van elk veld in `velden`? Zo niet (nog `undefined`, bv. de
 * Opmerking vanuit het Rooster vóór het verse record binnen is), dan bestaat er geen eerlijke
 * ongedaan-waarde — en zou schrijven een onbekende waarde overschrijven.
 */
export function kentVelden(taak, velden) {
  if (!taak) return false;
  return Object.keys(velden || {}).every((veld) => {
    const k = VELD_NAAR_SLEUTEL[veld];
    return !k || taak[k] !== undefined;
  });
}

/** De waarden die `velden` weer ongedaan maken — de `terug` voor biedOngedaanAan. */
export function terugVoor(taak, velden) {
  const t = taak || {};
  const terug = {};
  Object.keys(velden || {}).forEach((veld) => {
    switch (veld) {
      case 'NaamTaak': terug.NaamTaak = t.naam || ''; break;
      case 'Opmerking': terug.Opmerking = t.opmerking || ''; break;
      case 'PrioNr': terug.PrioNr = t.status || 'Geen prio'; break;
      case 'Afgerond': terug.Afgerond = Boolean(t.afgerond); break;
      case 'Type': terug.Type = t.type || null; break;
      case 'StartDateUitvoerder': terug.StartDateUitvoerder = t.start || null; break;
      case 'EndDateUitvoerder': terug.EndDateUitvoerder = t.eind || null; break;
      case 'Deadline': terug.Deadline = t.deadline || null; break;
      case 'TypeDeadline': terug.TypeDeadline = t.deadlineSoort || null; break;
      case 'UrenWerk': terug.UrenWerk = t.uren ?? null; break;
      case 'Assignee': terug.Assignee = [...(t.assigneeIds || [])]; break;
      default: break;
    }
  });
  return terug;
}

/**
 * Status kiezen: "Afgerond" staat twee keer in Airtable (checkbox én PrioNr). Zet je er één,
 * dan verdwijnt de taak op het ene scherm wél en op het andere niet — dus altijd samen.
 * Opgestuurd is géén afgerond (de klant kan nog terugkomen): checkbox blijft zoals hij is.
 */
export function statusVelden(taak, status) {
  if (status === 'Afgerond' || status === 'Gearchiveerd') return { PrioNr: status, Afgerond: true };
  if (status === 'Opgestuurd') return { PrioNr: status };
  if (taak?.afgerond) return { PrioNr: status, Afgerond: false };
  return { PrioNr: status };
}

/** Afvinkrondje: aan = Afgerond (beide velden), uit = terug naar open. */
export function afvinkVelden(taak) {
  if (taak?.afgerond) {
    const vorige = taak.status && !AFGEROND_STATUSSEN.has(taak.status) ? taak.status : 'Geen prio';
    return { Afgerond: false, PrioNr: vorige };
  }
  return { Afgerond: true, PrioNr: 'Afgerond' };
}

/** `loc_task_…` (Postgres-taak) en `outlook:…` bestaan niet in Airtable; alleen `rec…` wel. */
export const isAirtableId = (id) => /^rec[A-Za-z0-9]{14}$/.test(String(id || ''));
export const airtableUrl = (id) => (isAirtableId(id)
  ? `https://airtable.com/apphMT5j6gtUnMY1p/tblfs5bMNPZIwXJZY/${id}`
  : null);

/** Is de deadline voorbij (en de taak nog open)? Het enige rood in het paneel. */
export const isTeLaat = (taak, vandaagISO) => Boolean(
  // Een draaidag of vrije dag is nooit te laat: voorbij is voorbij (Joris, 3 nov 2026).
  taak && !taak.afgerond && !isDagstaatTaak(taak)
    && taak.deadline && vandaagISO && taak.deadline < vandaagISO,
);

/** Personen uit elke bron → `[{ id, label }]`, uniek op id en op naam gesorteerd. */
export function persoonOpties(personen) {
  const gezien = new Map();
  (personen || []).forEach((p) => {
    if (!p?.id || gezien.has(p.id)) return;
    const f = p.fields || {};
    const label = p.label || f['Volledige naam'] || f.Naam || f.Voornaam || p.name || p.naam
      || p.firstName || p.fullName || '';
    if (label) gezien.set(p.id, { id: p.id, label: String(label) });
  });
  return [...gezien.values()].sort((a, b) => a.label.localeCompare(b.label, 'nl'));
}

/** Kopregel onder de titel: "Project · deliverable/fase". */
export function contextRegel(taak) {
  if (!taak) return '';
  const tweede = taak.deliverableNaam || taak.fase || '';
  return [taak.projectNaam, tweede].filter(Boolean).join(' · ');
}

/** Links in een opmerking (Outlook-mail, Frame.io, …) — lange URL's zijn als tekst onleesbaar. */
export function linksUit(tekstje) {
  return [...new Set((String(tekstje || '').match(/https?:\/\/[^\s<>"')]+/g) || []))];
}
